import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { MapPin, Clock, ExternalLink, Star } from 'lucide-react';
import { mockData } from '../mock';
import '../styles/Home.css';

const Home = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="home-container">
      {/* Header */}
      <header className="site-header">
        <div className="header-content">
          <div className="logo-container">
            <img 
              src="https://customer-assets.emergentagent.com/job_9b161439-0bff-4ea8-aa6a-fca385c45d87/artifacts/rjst4zd8_ladoculto_logo.jpg" 
              alt="Ladoculto" 
              className="logo-img"
            />
          </div>
          <nav className="nav-links">
            <button onClick={() => scrollToSection('about')} className="nav-link">Sobre nosotros</button>
            <button onClick={() => scrollToSection('gastro')} className="nav-link">Gastronomía</button>
            <button onClick={() => scrollToSection('horarios')} className="nav-link">Horarios</button>
            <button onClick={() => scrollToSection('ubicacion')} className="nav-link">Ubicación</button>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className={`hero-section ${isVisible ? 'visible' : ''}`}>
        <div className="hero-content">
          <h1 className="hero-title">
            Café, libros, música y vinos.
          </h1>
          <p className="hero-subtitle">
            Un espacio cultural para disfrutar sin apuro en Rosario.
          </p>
          <div className="hero-buttons">
            <Button 
              className="btn-primary-hero"
              onClick={() => scrollToSection('ubicacion')}
            >
              <MapPin className="btn-icon" />
              Cómo llegar
            </Button>
            <Button 
              className="btn-secondary-hero"
              onClick={() => scrollToSection('horarios')}
            >
              <Clock className="btn-icon" />
              Ver horarios
            </Button>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="section-about">
        <div className="section-content">
          <h2 className="section-title">Sobre Ladoculto</h2>
          <div className="about-text">
            <p className="about-paragraph">
              {mockData.about.intro}
            </p>
            <p className="about-paragraph">
              {mockData.about.experience}
            </p>
            <p className="about-paragraph">
              {mockData.about.atmosphere}
            </p>
          </div>
        </div>
      </section>

      <Separator className="section-separator" />

      {/* Gastronomía Section */}
      <section id="gastro" className="section-gastro">
        <div className="section-content">
          <h2 className="section-title">Gastronomía & Bebidas</h2>
          <div className="gastro-grid">
            {mockData.gastro.highlights.map((item, index) => (
              <Card key={index} className="gastro-card">
                <CardContent className="gastro-card-content">
                  <h3 className="gastro-card-title">{item.title}</h3>
                  <p className="gastro-card-description">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="gastro-cta">
            <a 
              href="https://linktr.ee/ladocultorosario" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              <Button className="btn-primary-gastro">
                Ver carta completa
                <ExternalLink className="btn-icon" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      <Separator className="section-separator" />

      {/* Experiencia Section */}
      <section className="section-experience">
        <div className="section-content">
          <h2 className="section-title">Experiencia</h2>
          <div className="experience-content">
            <p className="experience-text">
              {mockData.experience.description}
            </p>
            <div className="experience-features">
              {mockData.experience.features.map((feature, index) => (
                <div key={index} className="feature-item">
                  <div className="feature-dot"></div>
                  <span className="feature-text">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Separator className="section-separator" />

      {/* Opiniones Section */}
      <section className="section-reviews">
        <div className="section-content">
          <h2 className="section-title">Lo que dicen de nosotros</h2>
          <div className="rating-container">
            <div className="rating-stars">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="star-icon" fill="#F9A08F" stroke="#F9A08F" />
              ))}
            </div>
            <p className="rating-text">5.0 / 5</p>
          </div>
          <div className="reviews-grid">
            {mockData.reviews.map((review, index) => (
              <Card key={index} className="review-card">
                <CardContent className="review-card-content">
                  <p className="review-text">"{review.text}"</p>
                  <p className="review-author">— {review.author}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <Separator className="section-separator" />

      {/* Horarios Section */}
      <section id="horarios" className="section-horarios">
        <div className="section-content">
          <h2 className="section-title">Horarios</h2>
          <div className="horarios-container">
            {mockData.schedule.map((item, index) => (
              <div key={index} className="horario-item">
                <div className="horario-days">{item.days}</div>
                <div className="horario-hours">{item.hours}</div>
              </div>
            ))}
          </div>
          <div className="horarios-note">
            <p>{mockData.scheduleNote}</p>
          </div>
        </div>
      </section>

      <Separator className="section-separator" />

      {/* Ubicación Section */}
      <section id="ubicacion" className="section-ubicacion">
        <div className="section-content">
          <h2 className="section-title">Ubicación</h2>
          <div className="ubicacion-info">
            <p className="ubicacion-address">
              <MapPin className="ubicacion-icon" />
              {mockData.location.address}
            </p>
            <div className="map-container">
              <iframe
                title="Mapa de Ladoculto"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3348.1234567890!2d-60.6416!3d-32.9417!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x95b7ab0f0f0f0f0f%3A0x0!2sZeballos%201460%2C%20S2000%20Rosario%2C%20Santa%20Fe!5e0!3m2!1ses!2sar!4v1234567890"
                width="100%"
                height="450"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
            <a 
              href="https://www.google.com/maps/dir//Zeballos+1460,+S2000+Rosario,+Santa+Fe" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              <Button className="btn-primary-ubicacion">
                <MapPin className="btn-icon" />
                Cómo llegar
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="site-footer">
        <div className="footer-simple">
          <p className="footer-copyright">© 2025 Ladoculto. Un lugar para quedarse, conversar y volver.</p>
          <p className="footer-credit">
            Desarrollado por{' '}
            <a 
              href="https://somosappolo.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="footer-link"
            >
              Appolo
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Home;